import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IoMenu,
  IoSettingsOutline,
  IoExitOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
} from "react-icons/io5";
import { RiAdminLine } from "react-icons/ri";
import logo from "../../assets/img/logo.svg";
import { HiOutlineUsers } from "react-icons/hi";
import { BiBriefcase } from "react-icons/bi";
import { IfHasPermission, usePermissions } from "context/PermissionsContext";
import { PermissionKeys } from "Constant/Constant";
import { clearSentryUser } from "utils/sentry";

export const Header = () => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    finance: false,
    operations: false,
    crm: false,
    clientManagement: false,
    usersAndAccess: false,
  });

  const matchesPath = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);
  const matchesAnyPath = (pathname, bases) => bases.some((base) => matchesPath(pathname, base));

  const isActive = (path, extraBases = []) =>
    matchesPath(location.pathname, path) || matchesAnyPath(location.pathname, extraBases);

  // Open groups based on active path on initial render
  useEffect(() => {
    const { pathname } = location;
    if (matchesAnyPath(pathname, ["/admin-fee", "/invoice"])) {
      setOpenGroups((prev) => ({ ...prev, finance: true }));
    } else if (matchesAnyPath(pathname, ["/orders", "/products"])) {
      setOpenGroups((prev) => ({ ...prev, operations: true }));
    } else if (matchesAnyPath(pathname, ["/inquiry", "/sales-leads", "/add-sales-leads", "/edit-sales-leads", "/request-sales-lead"])) {
      setOpenGroups((prev) => ({ ...prev, crm: true }));
    } else if (matchesAnyPath(pathname, ["/configuration", "/add-new-site", "/edit-config"])) {
      setOpenGroups((prev) => ({ ...prev, clientManagement: true }));
    } else if (matchesAnyPath(pathname, ["/sales-user", "/staff-user", "/permissions", "/create-sales-user", "/edit-sales-user", "/create-staff-user", "/edit-staff-user"])) {
      setOpenGroups((prev) => ({ ...prev, usersAndAccess: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const { allowedKeys, role, name } = usePermissions();

  const isAdmin = role === "Admin";

  const hasDashboard = isAdmin || allowedKeys.includes(PermissionKeys.VIEW_DASHBOARD);

  const hasFinance = isAdmin || [
    PermissionKeys.VIEW_ADMIN_FEE,
    PermissionKeys.VIEW_INVOICES,
  ].some((key) => allowedKeys.includes(key));

  const hasOperations = isAdmin || [
    PermissionKeys.VIEW_ORDERS,
    PermissionKeys.IMPORT_PRODUCTS,
  ].some((key) => allowedKeys.includes(key));

  const hasCRM = true;

  const hasClientManagement = isAdmin || [
    PermissionKeys.MANAGE_CONFIGURATIONS,
  ].some((key) => allowedKeys.includes(key));

  const hasUsersAndAccess = isAdmin || [
    PermissionKeys.MANAGE_SALES_USERS,
    PermissionKeys.MANAGE_STAFF_USERS,
    PermissionKeys.MANAGE_PERMISSIONS,
  ].some((key) => allowedKeys.includes(key));

  const hasAnyMenu = hasDashboard || hasFinance || hasOperations || hasCRM || hasClientManagement || hasUsersAndAccess;

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleLogoutClick = () => {
    setIsSidebarOpen(false);
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    clearSentryUser();
  };

  const SubMenuItem = ({ to, label, activeOn = [] }) => (
    <Link to={to} onClick={closeSidebar}>
      <div
        className={`flex justify-start items-center gap-2 py-2 pl-10 pr-2 rounded-lg transition-all duration-200 ${isActive(to, activeOn)
          ? "bg-secondary-button text-natural-header_text"
          : "bg-secondary-non_active_button"
          }`}>
        <span className="text-base tracking-wide capitalize">{label}</span>
      </div>
    </Link>
  );

  return (
    <React.Fragment>
      <header className="flex flex-col">
        <div className="w-full h-20 fixed flex justify-between items-center px-4 py-3 shadow-md bg-primary-header_bg z-10">
          <div className="flex items-center gap-3">
            {hasAnyMenu && (
              <IoMenu
                className="text-2xl md:hidden cursor-pointer"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              />
            )}
            <img src={logo} alt={"logo"} height={65} width={65} />
          </div>
          <div className="flex justify-center items-center gap-2">
            <span className="text-sm md:text-base font-medium text-natural-header_text hover:underline capitalize cursor-pointer">
              {name}
            </span>
            <RiAdminLine className="text-xl md:text-2xl text-natural-header_text font-bold" />
          </div>
        </div>

        {hasAnyMenu && (
          <div
            className={`${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
              } md:translate-x-0 flex flex-col h-[calc(100vh-4.5rem)] w-[280px] fixed top-[80px] left-0 shadow-xl z-20 overflow-y-auto px-3 py-4 bg-primary-slidbar_bg transition-transform duration-1000 gap-2 scrollbar-none`}>
            {hasDashboard && (
              <Link to="/" onClick={closeSidebar}>
                <div
                  className={`flex justify-start items-center gap-2 py-2 px-3 rounded-lg transition-all duration-200 ${isActive("/") ? "bg-secondary-button text-natural-header_text" : "bg-secondary-non_active_button"}`}>
                  <img src="/dashboard.svg" alt="dashboard" style={{ width: "20px" }} />
                  <span className="text-lg font-medium tracking-wider capitalize">Dashboard</span>
                </div>
              </Link>
            )}

            {/* Finance Group */}
            {hasFinance && (
              <div className="flex flex-col gap-1">
                <div
                  onClick={() => toggleGroup("finance")}
                  className="flex justify-between items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 bg-secondary-non_active_button">
                  <div className="flex gap-2 items-center">
                    <img src="/admin-fee.svg" alt="finance" style={{ width: "25px" }} />
                    <span className="text-[17px] font-medium tracking-wide capitalize">Finance</span>
                  </div>
                  {openGroups.finance ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                </div>
                {openGroups.finance && (
                  <div className="flex flex-col gap-1 mt-1">
                    <IfHasPermission permissionKey={PermissionKeys.VIEW_ADMIN_FEE}>
                      <SubMenuItem to="/admin-fee" label="Admin Fees" />
                    </IfHasPermission>
                    <IfHasPermission permissionKey={PermissionKeys.VIEW_INVOICES}>
                      <SubMenuItem to="/invoice" label="Invoices" />
                    </IfHasPermission>
                  </div>
                )}
              </div>
            )}

            {/* Operations Group */}
            {hasOperations && (
              <div className="flex flex-col gap-1">
                <div
                  onClick={() => toggleGroup("operations")}
                  className="flex justify-between items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 bg-secondary-non_active_button">
                  <div className="flex gap-2 items-center">
                    <BiBriefcase size={20} />
                    <span className="text-[17px] font-medium tracking-wide capitalize">Operations</span>
                  </div>
                  {openGroups.operations ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                </div>
                {openGroups.operations && (
                  <div className="flex flex-col gap-1 mt-1">
                    <IfHasPermission permissionKey={PermissionKeys.VIEW_ORDERS}>
                      <SubMenuItem to="/orders" label="Orders" />
                    </IfHasPermission>
                    <IfHasPermission permissionKey={PermissionKeys.IMPORT_PRODUCTS}>
                      <SubMenuItem to="/products" label="Import Products" />
                    </IfHasPermission>
                  </div>
                )}
              </div>
            )}

            {/* CRM Group */}
            {hasCRM && (
              <div className="flex flex-col gap-1">
                <div
                  onClick={() => toggleGroup("crm")}
                  className="flex justify-between items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 bg-secondary-non_active_button">
                  <div className="flex gap-2 items-center">
                    <HiOutlineUsers size={20} />
                    <span className="text-[17px] font-medium tracking-wide capitalize">CRM</span>
                  </div>
                  {openGroups.crm ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                </div>
                {openGroups.crm && (
                  <div className="flex flex-col gap-1 mt-1">
                    {/* <IfHasPermission permissionKey={PermissionKeys.VIEW_ALL_LEADS}> */}
                    <SubMenuItem
                      to="/sales-leads"
                      label="Sales Leads"
                      activeOn={["/add-sales-leads", "/edit-sales-leads", "/request-sales-lead"]}
                    />
                    {/* </IfHasPermission> */}
                    <IfHasPermission permissionKey={PermissionKeys.VIEW_INQUIRIES}>
                      <SubMenuItem to="/inquiry" label="Inquiries" />
                    </IfHasPermission>
                  </div>
                )}
              </div>
            )}

            {/* Client Management Group */}
            {hasClientManagement && (
              <div className="flex flex-col gap-1">
                <div
                  onClick={() => toggleGroup("clientManagement")}
                  className="flex justify-between items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 bg-secondary-non_active_button">
                  <div className="flex gap-2 items-center">
                    <IoSettingsOutline size={20} />
                    <span className="text-[17px] font-medium tracking-wide capitalize">
                      Client Management
                    </span>
                  </div>
                  {openGroups.clientManagement ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                </div>
                {openGroups.clientManagement && (
                  <div className="flex flex-col gap-1 mt-1">
                    <IfHasPermission permissionKey={PermissionKeys.MANAGE_CONFIGURATIONS}>
                      <SubMenuItem
                        to="/configuration"
                        label="Site Settings"
                        activeOn={["/add-new-site", "/edit-config"]}
                      />
                    </IfHasPermission>
                  </div>
                )}
              </div>
            )}

            {/* Users & Access Group */}
            {hasUsersAndAccess && (
              <div className="flex flex-col gap-1">
                <div
                  onClick={() => toggleGroup("usersAndAccess")}
                  className="flex justify-between items-center py-2 px-3 rounded-lg cursor-pointer transition-all duration-200 bg-secondary-non_active_button">
                  <div className="flex gap-2 items-center">
                    <RiAdminLine size={20} />
                    <span className="text-lg font-medium tracking-wider capitalize">
                      Users & Access
                    </span>
                  </div>
                  {openGroups.usersAndAccess ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                </div>
                {openGroups.usersAndAccess && (
                  <div className="flex flex-col gap-1 mt-1">
                    <IfHasPermission permissionKey={PermissionKeys.MANAGE_SALES_USERS}>
                      <SubMenuItem
                        to="/sales-user"
                        label="Sales Users"
                        activeOn={["/create-sales-user", "/edit-sales-user"]}
                      />
                    </IfHasPermission>
                    <IfHasPermission permissionKey={PermissionKeys.MANAGE_STAFF_USERS}>
                      <SubMenuItem
                        to="/staff-user"
                        label="Staff Users"
                        activeOn={["/create-staff-user", "/edit-staff-user"]}
                      />
                    </IfHasPermission>
                    <IfHasPermission permissionKey={PermissionKeys.MANAGE_PERMISSIONS}>
                      <SubMenuItem to="/permissions" label="Roles & Permissions" />
                    </IfHasPermission>
                  </div>
                )}
              </div>
            )}

            <div className="mt-auto pt-4">
              <hr className="border-gray-600 mb-2" />
              <Link to="/login" onClick={handleLogoutClick}>
                <div
                  className={`flex justify-between items-center px-4 py-3 rounded-md duration-200 bg-secondary-non_active_button hover:bg-secondary-button hover:text-natural-header_text`}>
                  <div className="text-sm md:text-base font-medium">Logout</div>
                  <div className="text-sm md:text-base font-medium">
                    <IoExitOutline size={25} />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        )}
      </header>
    </React.Fragment>
  );
};
